import { createTheme } from '@material-ui/core'

declare module '@material-ui/core/styles/createTheme' {
  interface TypographyProps {
    fontSize: number
    fontWeight: number
  }

  interface AppColors {
    text: {
      primary: string
      secondary: string
      light: string
    }
    input: {
      background: string
      backgroundFocus: string
      backgroundStrong: string
      backgroundMuted: string
      backgroundError: string
    }
    dropdown: {
      background: string
      hover: string
    }
    border: {
      light: string
      dark: string
    }
  }

  interface AppSpacing {
    xs: number
    sm: number
    md: number
    base: number
    lg: number
    xl: number
    xxl: number
  }

  interface AppBorderRadius {
    sm: number
    md: number
    lg: number
    xl: number
    full: number
  }

  // eslint-disable-next-line
  interface Theme {
    app: {
      colors: AppColors
      spacing: AppSpacing
      borderRadius: AppBorderRadius
      indication: {
        success: string
        warning: string
        danger: string
      }
      breakpoints: {
        xs: number
        sm: number
        md: number
        lg: number
        xl: number
        mainSize: number
      }
      typography: {
        fontFamily: {
          main: string
          secondary: string
        }
        title: TypographyProps
        subTitle: TypographyProps
        paragraph: TypographyProps
        span: TypographyProps
        error: TypographyProps
        label: TypographyProps
        body: TypographyProps
        small: TypographyProps
        h2: TypographyProps
        h3: TypographyProps
      }
    }
  }
  // eslint-disable-next-line
  interface ThemeOptions {
    app: {
      colors: AppColors
      spacing: AppSpacing
      borderRadius: AppBorderRadius
      indication: {
        success: string
        warning: string
        danger: string
      }
      breakpoints: {
        xs: number
        sm: number
        md: number
        lg: number
        xl: number
        mainSize: number
      }
      typography: {
        fontFamily: {
          main: string
          secondary: string
        }
        title: TypographyProps
        subTitle: TypographyProps
        paragraph: TypographyProps
        span: TypographyProps
        error: TypographyProps
        label: TypographyProps
        body: TypographyProps
        small: TypographyProps
        h2: TypographyProps
        h3: TypographyProps
      }
    }
  }
}

declare module '@material-ui/core/styles/createPalette' {
  // eslint-disable-next-line
  interface TypeBackground {
    default: string
    paper: string
    primary: SimplePaletteColorOptions
    secondary: SimplePaletteColorOptions
    tertiary: SimplePaletteColorOptions
    other: SimplePaletteColorOptions
  }
}

const theme = createTheme({
  typography: {
    fontFamily: 'Poppins, Open Sans, Arial, sans-serif'
  },
  palette: {
    primary: {
      main: '#784A42',
      contrastText: '#D3D3D3'
    },
    secondary: {
      main: '#D4C7BC',
      contrastText: '#784A42'
    },
    background: {
      primary: {
        main: '#E5E5E5',
        contrastText: '#784A42 '
      },
      secondary: {
        main: '#D4C7BC',
        contrastText: '#784A42'
      },
      tertiary: {
        main: '#885D5D',
        contrastText: '#FFF000'
      },
      other: {
        main: '#673535',
        contrastText: '#D4C7BC'
      }
    },
    text: {
      primary: '#FAFAFA',
      secondary: '#FFFFFF'
    },
    type: 'light'
  },
  app: {
    colors: {
      text: {
        primary: '#030A15',
        secondary: '#6B6662',
        light: '#FAFAFA'
      },
      input: {
        background: 'rgba(212, 199, 188, 0.3)',
        backgroundFocus: 'rgba(212, 199, 188, 0.4)',
        backgroundStrong: 'rgba(212, 199, 188, 0.5)',
        backgroundMuted: 'rgba(212, 199, 188, 0.2)',
        backgroundError: 'rgba(255, 0, 0, 0.05)'
      },
      dropdown: {
        background: '#F2F0EE',
        hover: '#D4C7BC'
      },
      border: {
        light: '#D3D3D3',
        dark: '#673535'
      }
    },
    spacing: {
      xs: 4,
      sm: 8,
      md: 12,
      base: 16,
      lg: 24,
      xl: 32,
      xxl: 40
    },
    borderRadius: {
      sm: 4,
      md: 8,
      lg: 10,
      xl: 12,
      full: 999
    },
    indication: {
      success: '#63BA90',
      warning: '#E9C855',
      danger: '#C8455D'
    },
    breakpoints: {
      xs: 321,
      sm: 641,
      md: 961,
      lg: 1281,
      xl: 1920,
      mainSize: 1440
    },
    typography: {
      fontFamily: {
        main: 'Poppins',
        secondary: 'Open Sans'
      },
      title: {
        fontSize: 40,
        fontWeight: 900
      },
      subTitle: {
        fontSize: 16,
        fontWeight: 300
      },
      paragraph: {
        fontSize: 13,
        fontWeight: 400
      },
      span: {
        fontSize: 24,
        fontWeight: 500
      },
      error: {
        fontSize: 11,
        fontWeight: 300
      },
      label: {
        fontSize: 14,
        fontWeight: 400
      },
      body: {
        fontSize: 16,
        fontWeight: 400
      },
      small: {
        fontSize: 12,
        fontWeight: 400
      },
      h2: {
        fontSize: 24,
        fontWeight: 600
      },
      h3: {
        fontSize: 18,
        fontWeight: 600
      }
    }
  },
  breakpoints: {
    values: {
      xs: 321,
      sm: 641,
      md: 961,
      lg: 1281,
      xl: 1920
    }
  }
})

export default theme
